"use client";

/**
 * Chunk 8.2 — ConvexProvider for apps/web.
 *
 * Reuses the same Convex deployment mobile uses (the dev/prod URLs
 * come from NEXT_PUBLIC_CONVEX_URL). This is the minimum delta for
 * web to consume Convex queries/mutations — specifically read-only
 * access to the playSession* tables (chunks 6.1/6.2) so that the
 * chunk 9 studio playtest viewport can observe a session as it
 * unfolds.
 *
 * Auth bridging deferred:
 *   Web uses Better Auth backed by Postgres (the existing setup);
 *   mobile uses Better Auth backed by Convex (@convex-dev/better-auth).
 *   These run as separate Better Auth instances against different
 *   stores. For chunk 8 web's Convex client runs unauthenticated —
 *   it can read playSession queries (no auth required) but
 *   appendMutation / join / setStatus reject unauthed callers
 *   (chunk 6.2 wired authComponent.getAuthUser).
 *
 *   Chunk 9's studio playtest button is where web needs to *write*
 *   to Convex. The auth bridge lands there — either by issuing
 *   Convex tokens from web's API routes or by switching web's
 *   Better Auth to the Convex-hosted instance mobile uses.
 *
 * Until then: web reads, mobile writes; mobile clients see each
 * other end-to-end (chunk 8 demo target).
 */

import { ConvexProvider, ConvexReactClient } from "convex/react";
import { type PropsWithChildren, useMemo } from "react";

export function WebConvexProvider({ children }: PropsWithChildren) {
  const client = useMemo(() => {
    const url = process.env.NEXT_PUBLIC_CONVEX_URL;
    if (!url) {
      // No-op fallback when the env var isn't set (dev environments
      // that haven't been wired to Convex yet). Returns null so the
      // provider renders children directly; downstream useConvex /
      // useQuery calls will throw their normal "no provider" error.
      return null;
    }
    return new ConvexReactClient(url);
  }, []);

  if (!client) {
    return <>{children}</>;
  }

  return <ConvexProvider client={client}>{children}</ConvexProvider>;
}
